import type { MouseEvent } from 'react';

import { Box, Button, Link, Text, VStack } from '@chakra-ui/react';
import { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { formatDocumentName } from '#src/utils/format-document-name';

export interface SubpagesBlockProps {
  parentPath: string;
  children: Array<{ id: string; path: string; label?: string }>;
  onCreateSubpage?: () => void;
  onNavigateToDocument?: (path: string) => void;
}

export function SubpagesBlock({
  parentPath,
  children,
  onCreateSubpage,
  onNavigateToDocument,
}: SubpagesBlockProps) {
  const getRelativePath = useCallback(
    (childPath: string) => {
      if (!parentPath) return childPath;
      if (childPath === parentPath) return '';
      const prefix = parentPath + '/';
      return childPath.startsWith(prefix)
        ? childPath.slice(prefix.length)
        : childPath;
    },
    [parentPath],
  );

  const formatChildLabel = useCallback(
    (childPath: string) => {
      const relative = getRelativePath(childPath);
      const formatted = formatDocumentName(relative).join(' › ');
      return formatted || relative || childPath;
    },
    [getRelativePath],
  );

  const handleNavigate = useCallback(
    (path: string) => (e: MouseEvent) => {
      if (typeof onNavigateToDocument !== 'function') return;
      e.preventDefault();
      onNavigateToDocument(path);
    },
    [onNavigateToDocument],
  );

  return (
    <Box>
      <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mb={1}>
        Subpages
      </Text>
      <Text fontSize="xs" color="fg.subtle" mb={2}>
        Links to child documents for this page will appear here.
      </Text>

      <VStack align="stretch" gap={1}>
        {children.length > 0 ? (
          children.map((child) => (
            <Link
              key={child.id}
              asChild
              fontSize="sm"
              color="blue.600"
              _hover={{ textDecoration: 'underline' }}
            >
              <RouterLink
                to={`/documents/${child.path}`}
                onClick={handleNavigate(child.path)}
              >
                {formatChildLabel(child.path)}
              </RouterLink>
            </Link>
          ))
        ) : (
          <Text fontSize="xs" color="fg.subtle" fontStyle="italic">
            No subpages yet.
          </Text>
        )}
      </VStack>

      <Button
        type="button"
        size="xs"
        mt={3}
        colorScheme="blue"
        onClick={onCreateSubpage}
        disabled={typeof onCreateSubpage !== 'function'}
      >
        + Create subpage
      </Button>
    </Box>
  );
}
